/**
 * SOLID Review
 * S — Good. Renders the first-party cookie-preferences dialog (necessary/advertising toggles +
 *     reject/save/accept actions) and derives the `IEntityCookieConsentCategories` payload to hand back via
 *     `onSave`. The local `ToggleRow` sub-component is a clean extraction of the repeated checkbox-row
 *     markup. One clear concern (present + collect the consent choice), though `buildCategories` embeds a
 *     business rule (analytics always denied, functionality always true) inside a presentational file.
 * O — Acceptable. A new visible category needs both a new `ToggleRow` usage and an edit to
 *     `buildCategories`'s literal object — not additive from outside.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Four props (`isOpen`, `initial`, `onSave`, `onClose`), all directly used, no unrelated
 *     concerns bundled in.
 * D — Strong. Depends only on the domain `IEntityCookieConsentCategories` type and React — no infrastructure
 *     imports; all persistence/CMP wiring is pushed to the caller (`CookieConsentProvider`).
 */
import React from 'react';
import type { IEntityCookieConsentCategories } from '../../application/ports/ClientVocabulary';
import { useViewModel } from '../viewmodels/useViewModel';
import { CookiePreferencesViewModel } from '../viewmodels/CookiePreferencesViewModel';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  initial: IEntityCookieConsentCategories;
  onSave: (categories: IEntityCookieConsentCategories) => void;
  onClose: () => void;
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  testId: string;
  onChange?: (checked: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ label, description, checked, disabled, testId, onChange }) => (
  <label
    style={{
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      padding: '10px 0',
      borderBottom: '1px solid rgba(99,102,241,0.15)',
      cursor: disabled ? 'default' : 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.checked)}
      style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
    />
    <span>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#f9fafb' }}>{label}</span>
      <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>{description}</span>
    </span>
  </label>
);

/**
 * First-party cookie preference centre. Shown as the fallback when no certified
 * CMP (Google Funding Choices) is configured, so consent is still adjustable.
 * Advertising groups the three Consent Mode ad signals; Analytics maps to
 * analytics_storage; strictly-necessary is always on.
 */
export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
  isOpen,
  initial,
  onSave,
  onClose,
}) => {
  // The toggle and its re-sync with the saved choice each time the modal opens are the model's (frontend-framework-oo P3, §28.295).
  const [{ advertising }, choice] = useViewModel(
    (views) => views.cookiePreferences(initial.adStorage && initial.adPersonalization),
    [isOpen, initial.adStorage, initial.adPersonalization],
  );

  if (!isOpen) return null;

  const btn = (bg: string, color: string): React.CSSProperties => ({
    flex: 1,
    padding: '9px 10px',
    borderRadius: 8,
    border: '1px solid rgba(99,102,241,0.5)',
    background: bg,
    color,
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'system-ui, sans-serif',
    cursor: 'pointer',
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      data-testid="cookie-prefs-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)',
          border: '1px solid rgba(99,102,241,0.45)', borderRadius: 16,
          width: 'min(460px, 94vw)', maxHeight: '85vh', display: 'flex',
          flexDirection: 'column', padding: 20, boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f9fafb' }}>Cookie preferences</span>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="cookie-prefs-close"
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 20, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: 'auto' }}>
          <ToggleRow
            label="Strictly necessary"
            description="Required to sign in, keep your session, and run the game. Always on."
            checked
            disabled
            testId="cookie-prefs-necessary-toggle"
          />
          <ToggleRow
            label="Advertising"
            description="Lets Google AdSense, Monetag and AdMob set identifiers to show ads. Off = no ad cookies."
            checked={advertising}
            testId="cookie-prefs-advertising-toggle"
            onChange={choice.setAdvertising}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            data-testid="cookie-prefs-reject-all"
            onClick={() => onSave(CookiePreferencesViewModel.categoriesOf(false))}
            style={btn('rgba(255,255,255,0.06)', '#d1d5db')}
          >
            Reject all
          </button>
          <button
            data-testid="cookie-prefs-save"
            onClick={() => onSave(CookiePreferencesViewModel.categoriesOf(advertising))}
            style={btn('rgba(99,102,241,0.15)', '#a5b4fc')}
          >
            Save choices
          </button>
          <button
            data-testid="cookie-prefs-accept-all"
            onClick={() => onSave(CookiePreferencesViewModel.categoriesOf(true))}
            style={btn('#6366f1', '#0b1020')}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
};
